import React, { useState, useRef } from 'react';
import {
  getCountry,
  resolveKycCountryCode,
  getKycIdentityConfig,
  maskTaxIdLast4,
} from '../../data/countries';
import { uploadKycDocument } from '../../utils/submissions';

const KYCModal = ({ onClose, onConfirm, country = "US" }) => {
  const [step, setStep] = useState(1);
  const kycCountry = resolveKycCountryCode(country);
  const idConfig = getKycIdentityConfig(kycCountry);
  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    taxIdLast4: "",
    address: "",
    cityRegionPostal: "",
    idType: "passport",
    idDocumentUrl: "",
    idDocumentPreview: "",
    idDocumentName: ""
  });
  const [errors, setErrors] = useState({});
  const [uploadingId, setUploadingId] = useState(false);
  const idFileRef = useRef(null);

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!formData.fullName.trim()) e.fullName = "Required";
      if (!formData.dob) e.dob = "Required";
      if (idConfig.taxIdRequired && (!formData.taxIdLast4 || formData.taxIdLast4.length < 4)) {
        e.taxIdLast4 = "Enter last 4 characters";
      }
    }
    if (s === 2) {
      if (!formData.address.trim()) e.address = "Required";
      if (!formData.cityRegionPostal.trim()) e.cityRegionPostal = "Required";
      if (!formData.idDocumentUrl) e.idDocument = "Upload a photo of your ID";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, 3)); };
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleIdUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, idDocument: "Please upload a JPEG or PNG image" }));
      return;
    }
    setUploadingId(true);
    try {
      const preview = URL.createObjectURL(file);
      const url = await uploadKycDocument(file);
      updateField("idDocumentUrl", url);
      updateField("idDocumentPreview", preview);
      updateField("idDocumentName", file.name);
      setErrors((prev) => { const n = { ...prev }; delete n.idDocument; return n; });
    } catch (err) {
      setErrors((prev) => ({ ...prev, idDocument: err.message || "Upload failed" }));
    } finally {
      setUploadingId(false);
      e.target.value = "";
    }
  };

  const submit = () => {
    onConfirm({
      fullName: formData.fullName.trim(),
      dob: formData.dob,
      taxIdLast4: String(formData.taxIdLast4 || "").replace(/\D/g, "").slice(-4),
      address: formData.address.trim(),
      cityRegionPostal: formData.cityRegionPostal.trim(),
      idType: formData.idType,
      idDocumentUrl: formData.idDocumentUrl,
      countryCode: kycCountry
    });
  };

  const steps = ["Personal Info", "Address & ID", "Review"];
  const idTypeLabel = (t) => String(t || "").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className="modal-overlay active" onClick={(e)=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:"500px"}}>
        <div className="modal-header">
          <h2>Verify Your Identity (KYC)</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="kyc-stepper">
            {steps.map((s, i) => (
              <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                <div className={`kyc-step-dot ${i+1===step?"active":i+1<step?"completed":""}`}>
                  {i+1 < step ? <i className="fas fa-check"></i> : i+1}
                </div>
                <div className={`kyc-step-label ${i+1===step?"active":""}`}>{s}</div>
              </div>
            ))}
          </div>

          {step === 1 && (
            <>
              <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: "1rem" }}>
                Verification for <strong>{getCountry(kycCountry).name || kycCountry}</strong>. We only store the last 4 digits of your tax/ID number.
              </p>
              <div className="form-group">
                <label>Full Legal Name</label>
                <input placeholder="As shown on ID" value={formData.fullName} onChange={(e)=>updateField("fullName", e.target.value)} />
                {errors.fullName && <div className="form-error">{errors.fullName}</div>}
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="date" value={formData.dob} onChange={(e)=>updateField("dob", e.target.value)} />
                {errors.dob && <div className="form-error">{errors.dob}</div>}
              </div>
              <div className="form-group">
                <label>{idConfig.taxIdLabel}</label>
                <input
                  placeholder={idConfig.taxIdPlaceholder}
                  maxLength={idConfig.taxIdMax}
                  value={formData.taxIdLast4}
                  onChange={(e)=>updateField("taxIdLast4", e.target.value.replace(/\D/g, "").slice(0, idConfig.taxIdMax))}
                />
                {errors.taxIdLast4 && <div className="form-error">{errors.taxIdLast4}</div>}
              </div>
              <div style={{display:"flex",gap:"1rem"}}>
                <button className="btn btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" style={{flex:1}} onClick={nextStep}>Continue</button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="form-group">
                <label>Home Address</label>
                <input placeholder="Street address" value={formData.address} onChange={(e)=>updateField("address", e.target.value)} />
                {errors.address && <div className="form-error">{errors.address}</div>}
              </div>
              <div className="form-group">
                <label>{idConfig.addressLine2Label}</label>
                <input placeholder={idConfig.addressLine2Label} value={formData.cityRegionPostal} onChange={(e)=>updateField("cityRegionPostal", e.target.value)} />
                {errors.cityRegionPostal && <div className="form-error">{errors.cityRegionPostal}</div>}
              </div>
              <div className="form-group">
                <label>ID Type</label>
                <select value={formData.idType} onChange={(e)=>updateField("idType", e.target.value)}>
                  <option value="passport">Passport</option>
                  <option value="national_id">National ID card</option>
                  <option value="drivers_license">Driver&apos;s licence</option>
                  <option value="residence_permit">Residence permit</option>
                </select>
              </div>
              <div className="form-group">
                <label>Upload ID</label>
                <input ref={idFileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleIdUpload} />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => !uploadingId && idFileRef.current?.click()}
                  onKeyDown={(ev) => ev.key === "Enter" && idFileRef.current?.click()}
                  style={{ border: "2px dashed var(--border)", borderRadius: "8px", padding: "1.5rem", textAlign: "center", color: "var(--text-light)", cursor: uploadingId ? "wait" : "pointer" }}
                >
                  {formData.idDocumentPreview ? (
                    <img src={formData.idDocumentPreview} alt="ID preview" style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 8, marginBottom: "0.5rem" }} />
                  ) : (
                    <i className={`fas fa-${uploadingId ? "spinner fa-spin" : "cloud-upload-alt"}`} style={{ fontSize: "2rem", marginBottom: "0.5rem", display: "block" }}></i>
                  )}
                  {uploadingId ? "Uploading…" : formData.idDocumentName ? formData.idDocumentName : "Click to upload ID photo (JPEG/PNG)"}
                </div>
                {errors.idDocument && <div className="form-error">{errors.idDocument}</div>}
              </div>
              <div style={{display:"flex",gap:"1rem"}}>
                <button className="btn btn-secondary" style={{flex:1}} onClick={prevStep}><i className="fas fa-arrow-left"></i> Back</button>
                <button className="btn btn-primary" style={{flex:1}} onClick={nextStep}>Continue</button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 style={{marginBottom:"1rem"}}>Review Your Information</h3>
              <p style={{color:"var(--text-light)",fontSize:"0.9rem",marginBottom:"1.5rem"}}>
                Please review everything carefully before submitting. You can go back to edit if needed.
              </p>
              <div style={{background:"var(--bg)",borderRadius:"12px",padding:"1.2rem",marginBottom:"1.5rem"}}>
                <div className="kyc-review-item"><label>Full Name</label><value>{formData.fullName || "—"}</value></div>
                <div className="kyc-review-item"><label>Date of Birth</label><value>{formData.dob || "—"}</value></div>
                <div className="kyc-review-item"><label>{idConfig.taxIdLabel}</label><value>{maskTaxIdLast4(formData.taxIdLast4)}</value></div>
                <div className="kyc-review-item"><label>Address</label><value>{formData.address || "—"}</value></div>
                <div className="kyc-review-item"><label>{idConfig.addressLine2Label}</label><value>{formData.cityRegionPostal || "—"}</value></div>
                <div className="kyc-review-item"><label>ID Type</label><value>{idTypeLabel(formData.idType)}</value></div>
                {formData.idDocumentPreview && (
                  <div style={{ marginTop: "1rem" }}>
                    <label style={{ display: "block", color: "var(--text-light)", fontSize: "0.9rem", marginBottom: "0.5rem" }}>ID document preview</label>
                    <img src={formData.idDocumentPreview} alt="Uploaded ID" style={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 8, border: "1px solid var(--border)" }} />
                  </div>
                )}
              </div>
              <p style={{ fontSize: "0.8rem", color: "var(--text-light)", marginBottom: "1rem" }}>
                An admin will review your submission. You can invest after approval (usually within 1–2 business days).
              </p>
              <div style={{display:"flex",gap:"1rem"}}>
                <button className="btn btn-secondary" style={{flex:1}} onClick={prevStep}><i className="fas fa-arrow-left"></i> Edit</button>
                <button className="btn btn-primary" style={{flex:1}} onClick={submit}>
                  <i className="fas fa-paper-plane"></i> Submit Verification
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCModal;
